<template>
  <Dialog :open="visible" @update:open="handleOpenChange">
    <DialogContent class="sm:max-w-[600px] p-0 gap-0">
      <DialogHeader class="p-6 pb-4 border-b border-border">
        <DialogTitle>{{ computedTitle }}</DialogTitle>
      </DialogHeader>

      <div class="flex flex-col gap-4 p-6" style="min-height: 500px">
        <div class="relative">
          <Search
            class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"
          />
          <Input
            v-model="searchQuery"
            :placeholder="$t('Поиск по названию или коду...')"
            class="pl-9 text-sm"
            @keyup.enter="handleSearch"
          />
        </div>

        <div v-if="loading" class="text-center py-8">
          <LoaderCircle
            class="w-4 h-4 animate-spin inline-block text-muted-foreground"
          />
          <div class="text-sm text-muted-foreground mt-2">
            {{ $t("Загрузка...") }}
          </div>
        </div>

        <div v-else-if="items.length === 0" class="text-center py-8">
          <FolderOpen
            class="w-10 h-10 text-muted-foreground mb-3 inline-block"
          />
          <div class="text-sm text-muted-foreground">
            {{
              searchQuery
                ? $t("Элементы не найдены")
                : $t("Нет доступных элементов")
            }}
          </div>
        </div>

        <!-- Items List -->
        <div v-else class="max-h-[400px] overflow-y-auto">
          <div class="space-y-1.5">
            <div
              v-for="item in items"
              :key="item.code"
              class="item-row"
              :class="{
                'item-row--active': selectedItem?.code === item.code,
              }"
              @click="selectItem(item)"
            >
              <div class="text-sm font-medium text-foreground">
                {{ getDisplayName(item) }}
              </div>
              <div class="text-xs text-muted-foreground mt-0.5">
                {{ $t("Код") }}: {{ item.code }}
                <span v-if="item.parent" class="">
                  → {{ getDisplayName(item.parent) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <DialogFooter
        class="p-6 pt-4 border-t border-border sm:justify-between items-center"
      >
        <div>
          <Pagination
            v-if="pagination.total > pagination.size"
            :total="pagination.total"
            :items-per-page="pagination.size"
            :page="pagination.page"
            :sibling-count="1"
            show-edges
            @update:page="handlePageChange"
          >
            <PaginationContent v-slot="{ items: pageItems }">
              <PaginationPrevious />
              <template v-for="(item, index) in pageItems" :key="index">
                <PaginationItem
                  v-if="item.type === 'page'"
                  :value="item.value"
                  :is-active="item.value === pagination.page"
                >
                  {{ item.value }}
                </PaginationItem>
                <PaginationEllipsis v-else />
              </template>
              <PaginationNext />
            </PaginationContent>
          </Pagination>
        </div>
        <div class="flex gap-2">
          <Button size="sm" variant="outline" @click="handleClose">
            {{ $t("Отмена") }}
          </Button>
          <Button
            size="sm"
            variant="default"
            :disabled="!selectedItem"
            @click="confirmSelection"
          >
            {{ $t("Выбрать") }}
          </Button>
        </div>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>

<script>
import {
  Search,
  LoaderCircle,
  Folder,
  FolderOpen,
  Check,
} from "lucide-vue-next";
import { getDirectoryList, getDirectoryTree } from "@/entities/directory";
import Button from "@/shared/ui/button/Button.vue";
import { Input } from "@/shared/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/shared/ui/dialog";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "@/shared/ui/pagination";

export default {
  name: "DirectorySelectorDialog",

  components: {
    Search,
    LoaderCircle,
    Folder,
    FolderOpen,
    Check,
    Button,
    Input,
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    Pagination,
    PaginationContent,
    PaginationEllipsis,
    PaginationItem,
    PaginationNext,
    PaginationPrevious,
  },

  props: {
    // Тип диалога: 'directories' или 'parents'
    type: {
      type: String,
      default: "directories",
      validator: (value) => ["directories", "parents"].includes(value),
    },
    // Коды для исключения
    excludeCodes: {
      type: Array,
      default: () => [],
    },
    // Код родителя для исключения
    excludeParentCode: {
      type: String,
      default: null,
    },
    // Кастомный заголовок
    title: {
      type: String,
      default: null,
    },
    // Для родителей: список категорий для выбора
    parentCategories: {
      type: Array,
      default: () => [],
    },
  },

  emits: ["select", "close"],

  data() {
    return {
      visible: false,
      searchQuery: "",
      loading: false,
      items: [],
      selectedItem: null,
      searchTimeout: null,
      pagination: {
        page: 1,
        size: 20,
        total: 0,
      },
    };
  },

  computed: {
    computedTitle() {
      if (this.title) return this.title;
      return this.type === "parents"
        ? this.$t("Выбор родительской категории")
        : this.$t("Выбор справочника");
    },

    isParentsMode() {
      return this.type === "parents";
    },

    isDirectoriesMode() {
      return this.type === "directories";
    },
  },

  watch: {
    searchQuery() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.pagination.page = 1;
        this.loadItems();
      }, 500);
    },
  },

  methods: {
    // Публичные методы для управления через ref
    open() {
      this.visible = true;
      this.searchQuery = "";
      this.selectedItem = null;
      this.pagination.page = 1;
      this.loadItems();
    },

    close() {
      this.visible = false;
      this.$emit("close");
    },

    handleOpenChange(open) {
      if (!open) {
        this.close();
      }
    },

    handleClose() {
      this.close();
    },

    getDisplayName(item) {
      if (!item) return "";
      return (
        item.name ||
        item.name_kz ||
        item.name_en ||
        item.code ||
        this.$t("Без названия")
      );
    },

    async loadItems() {
      this.loading = true;
      try {
        if (this.isParentsMode) {
          await this.loadParentCategories();
        } else {
          await this.loadDirectories();
        }
      } catch (error) {
        console.error("Error loading items:", error);
        this.items = [];
      } finally {
        this.loading = false;
      }
    },

    async loadDirectories() {
      const params = {
        page: this.pagination.page,
        size: this.pagination.size,
        parent_code: null,
      };

      if (this.searchQuery) {
        params.q = this.searchQuery;
      }

      const response = await getDirectoryList(params);

      if (response?.success && response.payload) {
        this.items = (response.payload.items || []).filter(
          (dir) =>
            !this.excludeCodes.includes(dir.code) &&
            dir.code !== this.excludeParentCode
        );
        this.pagination.total = response.payload.total || 0;
      }
    },

    async loadParentCategories() {
      // Если переданы категории, используем их
      if (this.parentCategories && this.parentCategories.length > 0) {
        this.items = this.filterItems(this.parentCategories);
        this.pagination.total = this.items.length;
        return;
      }

      // Иначе загружаем с сервера
      const data = await getDirectoryTree({
        page: this.pagination.page,
        size: this.pagination.size,
        include_children: false,
      });

      if (data?.success && data.payload?.items) {
        this.items = this.filterItems(data.payload.items);
        this.pagination.total = data.payload.total || 0;
      }
    },

    filterItems(items) {
      let filtered = items;

      // Фильтрация по поиску
      if (this.searchQuery) {
        const searchLower = this.searchQuery.toLowerCase();
        filtered = filtered.filter(
          (item) =>
            (item.name && item.name.toLowerCase().includes(searchLower)) ||
            (item.code && item.code.toLowerCase().includes(searchLower))
        );
      }

      // Исключаем коды
      if (this.excludeCodes.length > 0) {
        filtered = filtered.filter(
          (item) => !this.excludeCodes.includes(item.code)
        );
      }

      return filtered;
    },

    handleSearch() {
      this.pagination.page = 1;
      this.loadItems();
    },

    handlePageChange(page) {
      this.pagination.page = page;
      this.loadItems();
    },

    selectItem(item) {
      this.selectedItem = item;
    },

    confirmSelection() {
      if (this.selectedItem) {
        this.$emit("select", this.selectedItem);
        this.close();
      }
    },
  },
};
</script>

<style scoped lang="scss">
.item-row {
  @apply px-3 py-2.5 border border-border rounded-md cursor-pointer transition-colors;

  &:hover {
    @apply border-primary/40 bg-accent;
  }

  &--active {
    @apply border-primary bg-accent;
  }
}
</style>
